// scheduling/components/NonBillProfilePanel.jsx
// Stage 28I — Read-only non-billable budget review on EmployeeProfile.
// Year-round access for the staffer + their manager + admins. Same data
// shape as NonBillBudgetTab but no edit form (the cycle-gated subtab on
// /schedule/data-integrity is where editing happens).

import { useState, useEffect, useCallback } from 'react';
import { fetchNonBillCategories, fetchNonBillMyBudget } from '../services/schedData';
import NonBillBudgetChart from './NonBillBudgetChart';

function fmtFiscalYear(fyKey) {
  if (!fyKey) return '';
  const start = parseInt(fyKey, 10);
  return `May 1, ${start} – Apr 30, ${start + 1}`;
}
function fmtNum(v, decimals = 0) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
}
function fyDaysElapsed(fyKey) {
  const start = parseInt(fyKey, 10);
  const fyStart = new Date(start, 4, 1);
  const today = new Date();
  if (today < fyStart) return 0;
  const fyEnd = new Date(start + 1, 3, 30);
  if (today > fyEnd) return 365;
  return Math.floor((today - fyStart) / 86400000);
}

export default function NonBillProfilePanel({ staffId }) {
  const [categories, setCategories] = useState([]);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pacing toggle mirrors the budget tab. Linear is default; burn-rate uses
  // firm-wide CY pace factors per category from the backend.
  const [pacingMode, setPacingMode] = useState('linear');

  const reload = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    setError(null);
    try {
      const [cats, mine] = await Promise.all([
        fetchNonBillCategories(),
        fetchNonBillMyBudget(staffId),
      ]);
      setCategories(cats || []);
      setBudgetData(mine || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="sched-emp-panel">Loading non-billable budget…</div>;
  if (error) return <div className="sched-emp-panel" style={{ color: '#DC2626' }}>Error: {error}</div>;
  if (!budgetData) return <div className="sched-emp-panel">No non-billable data.</div>;

  const fy = budgetData.fiscalYear;
  const totalEffective = budgetData.totalEffective != null ? Number(budgetData.totalEffective) : null;
  const wipCY = budgetData.wipCY || {};
  const wipPY = budgetData.wipPY || {};
  const allocByCat = {};
  for (const a of (budgetData.allocations || [])) {
    allocByCat[a.CategoryKey] = Number(a.Hours);
  }
  const status = budgetData.budget?.Status || null;
  const daysElapsed = fyDaysElapsed(fy);
  const allocSum = Object.values(allocByCat).reduce((s, n) => s + n, 0);
  const cyTotal = Object.values(wipCY).reduce((s, n) => s + n, 0);
  const pyTotal = Object.values(wipPY).reduce((s, n) => s + n, 0);

  // Max bar width = max of (allocation, CY logged, PY actual) per row, used
  // to scale the inline bar chart so each metric is comparable visually.
  const rows = categories.map(c => {
    const key = c.CategoryKey;
    return {
      key,
      displayName: c.DisplayName,
      description: c.Description,
      allocation: allocByCat[key] || 0,
      cyActual: wipCY[key] || 0,
      pyActual: wipPY[key] || 0,
    };
  });
  const maxRowVal = rows.reduce((m, r) => Math.max(m, r.allocation, r.cyActual, r.pyActual), 1);

  // Pacing math:
  //   linear: allocation × (daysElapsed / 365). Naive even burn.
  //   burn:   allocation × expectedShare[cat]. expectedShare is the
  //           cumulative fraction of THIS user's (Dept × Level) cohort PY
  //           total that had been logged through this exact week of last FY,
  //           with intra-week linear interpolation. Captures real seasonality
  //           (PTO clusters around holidays, CPE pre-busy-season, etc.).
  //
  //           Categories without cohort PY data fall back to linear here
  //           (server omits them from expectedShare). When the cohort
  //           itself has no data, the server falls back to firmwide and
  //           sets expectedShareSource='firmwide' for tooltip messaging.
  const expectedShare = budgetData.expectedShare || {};
  const expectedShareSource = budgetData.expectedShareSource;          // 'cohort' | 'firmwide' | null
  const expectedShareCohort = budgetData.expectedShareCohort;          // { dept, level } or null
  function pacingExpected(row) {
    if (totalEffective == null || row.allocation <= 0) return 0;
    if (pacingMode === 'linear') return row.allocation * (daysElapsed / 365);
    const share = expectedShare[row.key];
    if (share == null || !Number.isFinite(share)) {
      // No cohort/firmwide PY data for this category — fall back to linear.
      return row.allocation * (daysElapsed / 365);
    }
    return row.allocation * share;
  }

  return (
    <div className="sched-emp-panel" style={{ borderRight: 'none' }}>
      <div className="nonbill-profile-head">
        <div>
          <h3 className="sched-emp-panel-title" style={{ marginBottom: 4 }}>
            Non-Billable Budget — FY {fy}
          </h3>
          <div className="nonbill-profile-fy-range">{fmtFiscalYear(fy)}</div>
        </div>
        <div>
          {totalEffective != null && (
            <div className="nonbill-profile-total">
              <strong>{fmtNum(totalEffective, 0)}</strong> hrs annual budget
              {budgetData.standardSource === 'override' && (
                <span className="nonbill-source-chip nonbill-source-chip--override">Admin Override Amount</span>
              )}
            </div>
          )}
          {status === 'submitted' ? <span className="idd-status-badge idd-status-badge--lg idd-status--confirmed">Submitted ✓</span>
            : status === 'draft' ? <span className="idd-status-badge idd-status-badge--lg idd-status--draft">Draft Saved</span>
            : <span className="idd-status-badge idd-status-badge--lg idd-status--pending">Not Started</span>}
        </div>
      </div>

      {totalEffective == null ? (
        <div className="idd-placeholder" style={{ marginTop: 12 }}>
          <strong>No standard configured</strong> for this staffer's department / level.
          Ask an admin to set up a standard or override before the budget can be allocated.
        </div>
      ) : (
        <>
          {/* Donut chart — only after submit per design. Drafts are still
              editable and might churn; the locked composition is what gets
              the visual treatment on the profile page. */}
          {status === 'submitted' && (
            <div className="nonbill-chart-card" style={{ marginBottom: 16 }}>
              <NonBillBudgetChart
                categories={categories}
                allocations={allocByCat}
                totalEffective={totalEffective}
                size={220}
                showLegend={true}
              />
            </div>
          )}

          <div className="nonbill-profile-summary-grid">
            <div className="nonbill-profile-summary-cell">
              <div className="nonbill-profile-summary-label">Allocated</div>
              <div className="nonbill-profile-summary-value">{fmtNum(allocSum, 0)} <span className="nonbill-profile-summary-out">/ {fmtNum(totalEffective, 0)}</span></div>
            </div>
            <div className="nonbill-profile-summary-cell">
              <div className="nonbill-profile-summary-label">CY Logged ({daysElapsed} days in)</div>
              <div className="nonbill-profile-summary-value">{fmtNum(cyTotal, 1)}</div>
            </div>
            <div className="nonbill-profile-summary-cell">
              <div className="nonbill-profile-summary-label">CY Pace (linear)</div>
              <div className="nonbill-profile-summary-value">{fmtNum((daysElapsed / 365) * totalEffective, 1)}</div>
            </div>
            <div className="nonbill-profile-summary-cell">
              <div className="nonbill-profile-summary-label">PY Total</div>
              <div className="nonbill-profile-summary-value">{fmtNum(pyTotal, 0)}</div>
            </div>
          </div>

          {/* Pacing toggle — mirrors the budget tab. */}
          <div className="nonbill-pacing-toggle" style={{ marginTop: 12 }}>
            Pacing:
            <label>
              <input type="radio" name="pacing-profile" value="linear"
                checked={pacingMode === 'linear'}
                onChange={() => setPacingMode('linear')} />
              Linear
            </label>
            <label>
              <input type="radio" name="pacing-profile" value="burn"
                checked={pacingMode === 'burn'}
                onChange={() => setPacingMode('burn')} />
              Burn-rate
            </label>
            <span className="nonbill-pacing-hint">
              {pacingMode === 'linear'
                ? 'Expected = allocation × (days elapsed / 365)'
                : Object.keys(expectedShare).length === 0
                  ? 'No PY data available — burn-rate falls back to linear.'
                  : expectedShareSource === 'firmwide'
                    ? 'Expected = allocation × % of firmwide PY total burned by this point (no PY data for your Dept × Level cohort yet).'
                    : `Expected = allocation × % of your ${expectedShareCohort?.dept || ''} L${expectedShareCohort?.level ?? ''} cohort's PY total burned by this point.`}
            </span>
          </div>

          <table className="nonbill-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Category</th>
                <th style={{ textAlign: 'right' }}>Budget</th>
                <th style={{ textAlign: 'right' }}>CY Logged</th>
                <th style={{ textAlign: 'right' }}>
                  Expected ({pacingMode === 'linear' ? 'Linear' : 'Burn-rate'})
                </th>
                <th style={{ textAlign: 'right' }}>PY Actual</th>
                <th>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const expected = pacingExpected(r);
                const variance = r.cyActual - expected;
                return (
                  <tr key={r.key}>
                    <td title={r.description || ''}>{r.displayName}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(r.allocation, 0)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {fmtNum(r.cyActual, 1)}
                      {Math.abs(variance) >= 1 && (
                        <span style={{
                          marginLeft: 4, fontSize: 11,
                          color: variance > 0 ? '#DC2626' : '#2563EB',
                        }}>
                          ({variance > 0 ? '+' : ''}{fmtNum(variance, 0)})
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', color: '#6B7280' }}>{fmtNum(expected, 1)}</td>
                    <td style={{ textAlign: 'right', color: '#6B7280' }}>{fmtNum(r.pyActual, 0)}</td>
                    <td style={{ minWidth: 200 }}>
                      <div className="nonbill-profile-bars">
                        <div className="nonbill-profile-bar nonbill-profile-bar--budget"
                          style={{ width: `${(r.allocation / maxRowVal) * 100}%` }}
                          title={`Budget: ${fmtNum(r.allocation, 0)}h`} />
                        <div className="nonbill-profile-bar nonbill-profile-bar--cy"
                          style={{ width: `${(r.cyActual / maxRowVal) * 100}%` }}
                          title={`CY: ${fmtNum(r.cyActual, 1)}h`} />
                        <div className="nonbill-profile-bar nonbill-profile-bar--py"
                          style={{ width: `${(r.pyActual / maxRowVal) * 100}%` }}
                          title={`PY: ${fmtNum(r.pyActual, 0)}h`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="nonbill-profile-legend">
            <span><span className="nonbill-profile-bar-swatch nonbill-profile-bar--budget" /> Budget</span>
            <span><span className="nonbill-profile-bar-swatch nonbill-profile-bar--cy" /> CY Logged</span>
            <span><span className="nonbill-profile-bar-swatch nonbill-profile-bar--py" /> PY Actual</span>
          </div>

          {!status && (
            <div className="idd-admin-empty" style={{ marginTop: 16 }}>
              No budget submitted yet for FY {fy}. Allocations can be made on the Data Integrity tab when a cycle is active.
            </div>
          )}
        </>
      )}
    </div>
  );
}
